import type { TFunction } from "i18next";
import type { AdminOffersRevenueTrendData } from "./adminOffersMappers";
import type {
  AdminHotelMetricRow,
  AdminTabMetricColumnDef,
} from "../types/adminHotelMetricTable";
import {
  downloadAnalyticsExport,
  type AnalyticsExportFormat,
  type AnalyticsExportTableSection,
} from "@/components/pages/analytics/front-desk/utils/analyticsFileExport";

function formatMetricCell(
  cell: { value: string; delta?: string } | undefined,
): string {
  if (!cell) {
    return "";
  }

  if (cell.delta) {
    return `${cell.value} (${cell.delta})`;
  }

  return cell.value;
}

export function buildAdminHotelsMetricExportSection(
  title: string,
  columns: AdminTabMetricColumnDef[],
  rows: AdminHotelMetricRow[],
  t: TFunction,
  summaryRows: AdminHotelMetricRow[] = [],
): AnalyticsExportTableSection {
  const mapRow = (row: AdminHotelMetricRow) => [
    row.hotelBrand,
    ...columns.map((column) => formatMetricCell(row.metrics[column.key])),
  ];

  return {
    title,
    headers: [
      t("analytics.admin.table.hotelBrand", "Hotel Brand"),
      ...columns.map((column) => t(column.headerKey, column.headerFallback)),
    ],
    rows: rows.map(mapRow),
    summaryRows: summaryRows.map(mapRow),
  };
}

export function buildAdminReportContextSection(
  params: {
    reportName: string;
    reportType: string;
    tenantName: string;
    period: string;
    appliedFilters: string;
  },
  t: TFunction,
): AnalyticsExportTableSection {
  return {
    title: t("analytics.admin.export.contextTitle", "Report context"),
    headers: [
      t("analytics.admin.export.contextField", "Field"),
      t("analytics.admin.export.contextValue", "Value"),
    ],
    rows: [
      [
        t("analytics.admin.export.reportName", "Report name"),
        params.reportName,
      ],
      [
        t("analytics.admin.export.reportType", "Report type"),
        params.reportType,
      ],
      [t("analytics.admin.export.tenant", "Tenant"), params.tenantName],
      [
        t("analytics.admin.export.selectedPeriod", "Selected period"),
        params.period,
      ],
      [
        t("analytics.admin.export.appliedFilters", "Applied filters"),
        params.appliedFilters,
      ],
    ],
  };
}

export function downloadAdminHotelsMetricExport(params: {
  format: AnalyticsExportFormat;
  filenameBase: string;
  sections: AnalyticsExportTableSection[];
}): void {
  downloadAnalyticsExport(params.format, params.sections, params.filenameBase);
}

export function buildAdminOffersRevenueTrendExportSection(
  trend: AdminOffersRevenueTrendData,
  t: TFunction,
): AnalyticsExportTableSection {
  return {
    title: t("analytics.offers.roomUpgrade.revenueTrend", "Revenue Trend"),
    headers: [
      t("analytics.common.day", "Day"),
      ...trend.hotels.map((hotel) => hotel.hotelName),
    ],
    rows: trend.rows.map((row) => [
      row.day,
      ...trend.hotels.map((hotel) => row[hotel.hotelId] ?? ""),
    ]),
  };
}

export function downloadAdminOffersSectionExport(params: {
  format: AnalyticsExportFormat;
  filenameBase: string;
  sectionTitle: string;
  columns: AdminTabMetricColumnDef[];
  rows: AdminHotelMetricRow[];
  t: TFunction;
}): void {
  const section = buildAdminHotelsMetricExportSection(
    params.sectionTitle,
    params.columns,
    params.rows,
    params.t,
  );

  downloadAdminHotelsMetricExport({
    format: params.format,
    filenameBase: params.filenameBase,
    sections: [section],
  });
}
